// ENGINE-LOCAL-FIRST — local attempt mirror (offline source of truth).
//
// Root cause this fixes (traced 2026-06-22): the ONLY attempt write/read
// path went through Supabase. In the local-first / offline reality the
// session screens' recordAttempt early-returns on `!persistEnabled`
// (no real auth session) and on a null canonical id, so NO attempt is
// ever persisted; and every engine read (useTodaysMission / useSkillGraph
// / PathHome trail) keys off the canonical cloud students.id, which is
// null/empty offline. Empty history => the fresh-learner fallback fires
// every session => identical focal skill => identical questions, and the
// parent skill tree stays "0/N mastered".
//
// This module is a tiny localStorage-backed mirror of what `child_attempts`
// would hold, keyed by the LOCAL child id (app.activeChildId) — the SAME id
// the per-child progress store (childrenStore: pencilwise-child-<id>) uses.
// Writes append here on every answer regardless of cloud auth; the engine
// read sites merge it with whatever the cloud returned. The pure
// aggregate/selection/mastery functions are untouched — they just receive
// real (local) history again.
//
// Cloud sync is intentionally OUT OF SCOPE (founder-confirmed local-first):
// the store is shaped so a future additive sync layer can upload these rows
// as the cross-device/backup path. Nothing here precludes that.

import type { ChildAttempt } from "@/adaptation/types";

const PREFIX = "pencilwise-attempts-";
// Bound storage growth. Engine reads at most 500; we keep a generous tail
// so cross-session history survives many sessions before the oldest rows
// roll off. ~1000 attempt rows is well within the localStorage budget.
const CAP = 1000;

const keyFor = (childId: string) => `${PREFIX}${childId}`;

// MA-980 (D-1013-20): the read reports `unreadable` (getItem threw, the JSON
// did not parse, or the value is not an array) separately from empty. readRaw
// keeps its [] result for every existing caller; only the strict exports below
// surface the flag.
function readRawResult(childId: string | null | undefined): { rows: ChildAttempt[]; unreadable: boolean } {
  if (typeof window === "undefined" || !childId) return { rows: [], unreadable: false };
  try {
    const raw = window.localStorage.getItem(keyFor(childId));
    if (!raw) return { rows: [], unreadable: false };
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? { rows: parsed as ChildAttempt[], unreadable: false }
      : { rows: [], unreadable: true };
  } catch {
    return { rows: [], unreadable: true };
  }
}

function readRaw(childId: string | null | undefined): ChildAttempt[] {
  return readRawResult(childId).rows;
}

function byTimestampAsc(a: ChildAttempt, b: ChildAttempt): number {
  return Date.parse(a.timestamp) - Date.parse(b.timestamp);
}

/** A row's sync identity — stable across cloud/local copies of the SAME
 *  attempt, independent of its timestamp's position relative to any other
 *  row. Used both by mergeAttempts' de-dup and by the drain confirm-set
 *  below (MA-376) — the same formula must never diverge between the two. */
export function attemptDedupKey(a: ChildAttempt): string {
  return `${a.runId ?? ""}|${a.itemId ?? ""}|${a.timestamp ?? ""}`;
}

// ── MA-376: drain confirm-set (replaces a timestamp-ordered cursor) ────
//
// outboxDrain used to track "synced so far" as a single max-confirmed
// TIMESTAMP and filter `row.timestamp > cursor`. That silently and
// PERMANENTLY dropped rows in two real cases: (a) a device clock set
// backward (kid tablets; a parent fixing a wrong clock) writes new rows
// whose timestamp is BEFORE the cursor — they never satisfy `> cursor` and
// are never drained; (b) two rows sharing one millisecond where only the
// first confirms — the cursor lands exactly ON that shared timestamp, so
// the second row's `> cursor` compare is false forever too.
//
// Tracking confirmed-ness by IDENTITY KEY instead of timestamp ordering
// makes both cases impossible: "already uploaded" is a fact about a row,
// never about its position relative to a moving cursor. Re-sending an
// already-confirmed row is a safe no-op (MA-14 upsert), so this errs
// toward re-sending on any doubt rather than silently dropping.
const CONFIRMED_PREFIX = "pencilwise-attempts-confirmed-";
const EVICTED_UNDRAINED_PREFIX = "pencilwise-attempts-evicted-undrained-";

function loadConfirmedKeysResult(childId: string): { keys: Set<string>; unreadable: boolean } {
  if (typeof window === "undefined") return { keys: new Set(), unreadable: false };
  try {
    const raw = window.localStorage.getItem(`${CONFIRMED_PREFIX}${childId}`);
    if (!raw) return { keys: new Set(), unreadable: false };
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? { keys: new Set(parsed), unreadable: false }
      : { keys: new Set(), unreadable: true };
  } catch {
    return { keys: new Set(), unreadable: true };
  }
}

function loadConfirmedKeys(childId: string): Set<string> {
  return loadConfirmedKeysResult(childId).keys;
}

function saveConfirmedKeys(childId: string, keys: Set<string>): void {
  if (typeof window === "undefined") return;
  try {
    // Bound growth to the mirror's own cap — a key for a row that has
    // already rolled off the mirror can never be looked up again.
    const arr = Array.from(keys).slice(-CAP);
    window.localStorage.setItem(`${CONFIRMED_PREFIX}${childId}`, JSON.stringify(arr));
  } catch {
    /* best-effort */
  }
}

/** Mark rows as confirmed-synced. Called by outboxDrain after a successful
 *  upload (and for malformed rows, which can never sync and must not be
 *  retried forever). */
export function markAttemptsConfirmed(
  childId: string | null | undefined,
  attempts: readonly ChildAttempt[],
): void {
  if (typeof window === "undefined" || !childId || attempts.length === 0) return;
  const keys = loadConfirmedKeys(childId);
  for (const a of attempts) keys.add(attemptDedupKey(a));
  saveConfirmedKeys(childId, keys);
}

/** Load attempts NOT YET confirmed-synced — outboxDrain's upload candidate
 *  set, chronological. Replaces the old ">last confirmed timestamp" cursor
 *  filter (see the confirm-set comment above). A healthy periodic tick
 *  (nothing new, nothing stuck) sees an empty confirmed-complement and
 *  costs nothing beyond the Set lookups. */
export function loadUnconfirmedAttempts(
  childId: string | null | undefined,
  limit = CAP,
): ChildAttempt[] {
  // MA-980 (RED r1 S1): one body, so the kid card and the drain can never
  // count differently; the rows are exactly the strict twin's rows.
  return tryLoadUnconfirmedAttempts(childId, limit).rows;
}

/** MA-980 (D-1013-20): loadUnconfirmedAttempts' strict twin for the parent
 *  kid card (loadUnconfirmedAttempts returns its `.rows`). `unreadable` is
 *  true when this child's attempt store could not be read, or when its
 *  confirmed-key list could not be read while there are attempt rows that
 *  list would gate (RED r1 N1: the same rule the milestone list follows,
 *  which readKidOutbox reads only when a drainable milestone exists). */
export function tryLoadUnconfirmedAttempts(
  childId: string | null | undefined,
  limit = CAP,
): { rows: ChildAttempt[]; unreadable: boolean } {
  if (!childId) return { rows: [], unreadable: false };
  const confirmed = loadConfirmedKeysResult(childId);
  const raw = readRawResult(childId);
  const sorted = [...raw.rows].sort(byTimestampAsc);
  const unconfirmed = sorted.filter((a) => !confirmed.keys.has(attemptDedupKey(a)));
  return {
    rows: unconfirmed.slice(Math.max(0, unconfirmed.length - limit)),
    unreadable: raw.unreadable || (confirmed.unreadable && raw.rows.length > 0),
  };
}

function loadEvictedUndrainedCount(childId: string): number {
  if (typeof window === "undefined") return 0;
  try {
    const raw = window.localStorage.getItem(`${EVICTED_UNDRAINED_PREFIX}${childId}`);
    const n = raw ? Number.parseInt(raw, 10) : 0;
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function recordEvictedUndrained(childId: string, additional: number): void {
  if (typeof window === "undefined" || additional <= 0) return;
  try {
    const total = loadEvictedUndrainedCount(childId) + additional;
    window.localStorage.setItem(`${EVICTED_UNDRAINED_PREFIX}${childId}`, String(total));
  } catch {
    /* best-effort */
  }
}

/** Read-and-clear the undrained-eviction counter for `childId` — a
 *  one-shot consume so a caller (useOutboxDrain) can surface ONE calm
 *  parent-visible signal the next time it mounts, then not repeat it every
 *  boot. Returns 0 (no-op) when nothing was ever evicted un-drained. */
export function consumeUndrainedEvictionCount(childId: string | null | undefined): number {
  if (typeof window === "undefined" || !childId) return 0;
  const n = loadEvictedUndrainedCount(childId);
  if (n > 0) {
    try { window.localStorage.removeItem(`${EVICTED_UNDRAINED_PREFIX}${childId}`); } catch { /* best-effort */ }
  }
  return n;
}

/** Trim `arr` to CAP, evicting already-CONFIRMED rows first (oldest-first —
 *  they're already safe in the cloud) and falling through to evicting
 *  UNCONFIRMED rows only when there aren't enough confirmed rows to absorb
 *  the overflow (MA-376 c: a long fully-offline stretch that outgrows the
 *  cap before ever syncing). The un-drained-loss case is counted via
 *  recordEvictedUndrained rather than silently dropped. */
function trimToCap(childId: string, arr: ChildAttempt[]): ChildAttempt[] {
  if (arr.length <= CAP) return arr;
  const confirmed = loadConfirmedKeys(childId);
  let toEvict = arr.length - CAP;
  const kept: ChildAttempt[] = [];
  for (const a of arr) {
    if (toEvict > 0 && confirmed.has(attemptDedupKey(a))) {
      toEvict -= 1;
      continue;
    }
    kept.push(a);
  }
  let evictedUndrained = 0;
  while (kept.length > CAP) {
    kept.shift();
    evictedUndrained += 1;
  }
  if (evictedUndrained > 0) recordEvictedUndrained(childId, evictedUndrained);
  return kept;
}

/** Append one attempt to the local mirror for `childId`. Best-effort:
 *  swallows quota / serialization errors exactly like the cloud logger's
 *  permanent-failure branch (a dropped mirror write must never crash the
 *  session). No-op under SSR / when childId is null. */
export function appendLocalAttempt(
  childId: string | null | undefined,
  attempt: ChildAttempt,
): void {
  if (typeof window === "undefined" || !childId) return;
  try {
    const arr = readRaw(childId);
    arr.push(attempt);
    const capped = trimToCap(childId, arr);
    window.localStorage.setItem(keyFor(childId), JSON.stringify(capped));
  } catch {
    /* best-effort */
  }
}

/** Delete the entire local attempt mirror for `childId` (used by
 *  removeChild so a deleted kid leaves no orphaned attempt history on the
 *  device — B-2/G-6). No-op under SSR / when childId is null. */
export function removeLocalAttempts(childId: string | null | undefined): void {
  if (typeof window === "undefined" || !childId) return;
  try { window.localStorage.removeItem(keyFor(childId)); } catch { /* best-effort */ }
}

/** Load the child's local attempts, chronological (oldest -> newest) to
 *  match attemptLogger.fetchAllAttemptsForChild's order, capped to `limit`
 *  most-recent rows. */
export function loadLocalAttempts(
  childId: string | null | undefined,
  limit = 500,
): ChildAttempt[] {
  if (!childId) return [];
  const sorted = [...readRaw(childId)].sort(byTimestampAsc);
  return sorted.slice(Math.max(0, sorted.length - limit));
}

/** Load the child's local attempts for one skill, chronological, capped.
 *  Mirrors attemptLogger.fetchRecentAttempts' (child, skill) shape. */
export function loadLocalAttemptsForSkill(
  childId: string | null | undefined,
  skillId: string,
  limit = 50,
): ChildAttempt[] {
  if (!childId) return [];
  const sorted = readRaw(childId)
    .filter((a) => a.skillId === skillId)
    .sort(byTimestampAsc);
  return sorted.slice(Math.max(0, sorted.length - limit));
}

/** MA-303b RED round 2 (S1(b)) — the local-only counterpart to
 *  `fetchTransferAttemptsForSkillOrThrow`: the child's ENTIRE local
 *  tagged-transfer history for one skill, filtered FIRST (never windowed
 *  first, then filtered) so a guest/local-only/offline profile — which has
 *  no cloud row to fetch from at all — gets the same "does a tagged FAIL
 *  ever scroll out of the evidence" protection a linked profile gets from
 *  the cloud fetch. `loadLocalAttemptsForSkill`/`loadLocalAttempts` are
 *  BOTH bounded (limit 50 / 500) exactly like the two cloud windows R1 was
 *  written to escape — reading through them first and filtering after
 *  would reopen the identical loophole for this profile shape. No cap
 *  here: this reads the SAME already-capped local store
 *  (MAX_ROWS_PER_CHILD, ~1000) every other local reader uses, and tagged
 *  rows are rare within it. */
export function loadLocalTaggedTransferAttempts(
  childId: string | null | undefined,
  skillId: string,
): ChildAttempt[] {
  if (!childId) return [];
  return readRaw(childId)
    .filter((a) => a.skillId === skillId && (a.isTransferTask || a.transferTaskId !== null))
    .sort(byTimestampAsc);
}

/** Merge cloud + local attempt lists, de-duped and chronological. A cloud
 *  row and its local mirror are the SAME attempt; we key on
 *  runId|itemId|timestamp so a synced row isn't double-counted. Cloud wins
 *  on a collision (it's the durable copy once sync exists). Returns oldest
 *  -> newest so downstream aggregators/windowing see chronological order. */
export function mergeAttempts(
  cloud: readonly ChildAttempt[],
  local: readonly ChildAttempt[],
): ChildAttempt[] {
  const seen = new Set<string>();
  const dedupKey = attemptDedupKey;
  const out: ChildAttempt[] = [];
  for (const a of cloud) {
    const k = dedupKey(a);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(a);
    }
  }
  for (const a of local) {
    const k = dedupKey(a);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(a);
    }
  }
  return out.sort(byTimestampAsc);
}
